import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Instagram } from "lucide-react";
import { api, mediaUrl } from "../../lib/api";

const igHandle = (url) => {
  const match = String(url || "").match(/instagram\.com\/([^/?#]+)/);
  return match ? `@${match[1].replace(/\/$/, "")}` : "Instagram";
};

export default function InstagramSection({ instagramUrl, gallery }) {
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    api
      .get("/instagram/feed")
      .then((r) => setFeed(r.data?.items || []))
      .catch(() => setFeed([]));
  }, []);

  if (!instagramUrl) return null;

  // Live Instagram photos when the API is connected; gallery strip as fallback
  const galleryPhotos = (gallery || []).filter((item) => item.image_url).slice(0, 5);
  const useFeed = feed && feed.length > 0;
  const tiles = useFeed
    ? feed.map((item) => ({ key: item.id, url: item.permalink, image: item.image_url, alt: "Instagram post by Nusadira Lembongan Villa" }))
    : galleryPhotos.map((item, i) => ({
        key: item.id || i,
        url: instagramUrl,
        image: mediaUrl(item.image_url),
        alt: item.image_alt || item.caption || "Nusadira Lembongan Villa",
        focal: item.image_focal_point || "50% 0%",
      }));

  return (
    <section data-testid="instagram-section" className="py-20 sm:py-24 bg-[#FBF9F5] border-t border-[#E5DED4]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 text-center">
        <motion.span
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="inline-flex w-12 h-12 rounded-full bg-[#9A7B66]/10 text-[#9A7B66] items-center justify-center mb-5"
        >
          <Instagram className="w-5 h-5" />
        </motion.span>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="font-serif font-normal tracking-tight text-[#2C2825] leading-tight text-3xl sm:text-4xl"
        >
          Follow Us on Instagram
        </motion.h2>
        <motion.a
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          data-testid="instagram-handle-link"
          href={instagramUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-block mt-3 text-sm font-sans font-semibold text-[#9A7B66] hover:text-[#826552] tracking-[0.08em] transition-colors"
        >
          {igHandle(instagramUrl)}
        </motion.a>

        {tiles.length > 0 && (
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {tiles.map((tile, i) => (
              <motion.a
                key={tile.key}
                data-testid={`instagram-photo-${i}`}
                href={tile.url}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group relative aspect-square rounded-md overflow-hidden"
              >
                <img
                  src={tile.image}
                  alt={tile.alt}
                  loading="lazy"
                  style={tile.focal ? { objectPosition: tile.focal } : undefined}
                  className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                />
                <span className="absolute inset-0 bg-[#1E1A17]/0 group-hover:bg-[#1E1A17]/35 transition-colors duration-300 flex items-center justify-center">
                  <Instagram className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </span>
              </motion.a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
